import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const PageContainer = styled.div`
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 12px;
  padding: 40px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 215, 0, 0.2);
  backdrop-filter: blur(3px);
`;

const Title = styled.h1`
  font-size: 42px;
  margin-bottom: 40px;
  text-align: center;
  color: var(--gold-color);
  text-shadow: 0 2px 10px rgba(255, 215, 0, 0.5);
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const FormGroup = styled.div`
  margin-bottom: 25px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 10px;
  font-size: 18px;
  color: var(--gold-color);
  font-weight: bold;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
`;

const Input = styled.input`
  background-color: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 215, 0, 0.3);
  color: var(--gold-color);
  padding: 15px;
  font-size: 18px;
  border-radius: 6px;
  margin-bottom: 5px;
  width: 100%;
  transition: all 0.3s ease;
  font-family: var(--main-font);
  
  &:focus {
    outline: none;
    border-color: var(--gold-color);
    box-shadow: 0 0 10px rgba(255, 215, 0, 0.3);
  }
`;

const InputControls = styled.div`
  position: absolute;
  right: 10px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ControlButton = styled.button`
  background: none;
  border: none;
  color: var(--gold-color);
  font-size: 16px;
  cursor: pointer;
  padding: 2px 8px;
  opacity: 0.7;
  transition: all 0.2s ease;
  
  &:hover {
    opacity: 1;
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const SubmitButton = styled.button`
  margin-top: 30px;
  font-size: 20px;
  padding: 15px;
  background: rgba(212, 175, 55, 0.8);
  border: none;
  color: #000;
  font-weight: bold;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  font-family: var(--main-font);
  
  &:hover {
    background: rgba(255, 215, 0, 0.9);
    transform: translateY(-2px);
    box-shadow: 0 6px 15px rgba(0, 0, 0, 0.3);
  }
  
  &:active {
    transform: translateY(1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }
`;

const InputPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    monthlySalary: 10000,
    hoursPerDay: 8,
    workdaysPerMonth: 22
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: parseFloat(value) || 0
    });
  };
  
  const handleIncrement = (field, step = 1) => {
    setFormData({
      ...formData,
      [field]: Math.round((formData[field] + step) * 100) / 100
    });
  };
  
  const handleDecrement = (field, step = 1) => {
    const minValues = {
      monthlySalary: 1,
      hoursPerDay: 0.5,
      workdaysPerMonth: 1
    };
    
    const newValue = Math.max(minValues[field], Math.round((formData[field] - step) * 100) / 100);
    
    setFormData({
      ...formData,
      [field]: newValue
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // 保存数据到 localStorage
    localStorage.setItem('salaryData', JSON.stringify(formData));
    
    // 导航到工作页面
    navigate('/working');
  };

  return (
    <PageContainer>
      <Title>实时工资计算器</Title>
      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="monthlySalary">月薪 (人民币)</Label>
          <InputWrapper>
            <Input
              type="number"
              id="monthlySalary"
              name="monthlySalary"
              value={formData.monthlySalary}
              onChange={handleChange}
              min="1"
            />
            <InputControls>
              <ControlButton 
                type="button" 
                onClick={() => handleIncrement('monthlySalary', 100)}
              >
                +
              </ControlButton>
              <ControlButton 
                type="button" 
                onClick={() => handleDecrement('monthlySalary', 100)}
              >
                -
              </ControlButton>
            </InputControls>
          </InputWrapper>
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="hoursPerDay">每天工作小时数</Label>
          <InputWrapper>
            <Input
              type="number"
              id="hoursPerDay"
              name="hoursPerDay"
              value={formData.hoursPerDay}
              onChange={handleChange}
              min="0.5"
              step="0.5"
            />
            <InputControls>
              <ControlButton 
                type="button" 
                onClick={() => handleIncrement('hoursPerDay', 0.5)}
              >
                +
              </ControlButton>
              <ControlButton 
                type="button" 
                onClick={() => handleDecrement('hoursPerDay', 0.5)}
              >
                -
              </ControlButton>
            </InputControls>
          </InputWrapper>
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="workdaysPerMonth">当月上班天数</Label>
          <InputWrapper>
            <Input
              type="number"
              id="workdaysPerMonth"
              name="workdaysPerMonth"
              value={formData.workdaysPerMonth}
              onChange={handleChange}
              min="1"
              max="31"
            />
            <InputControls>
              <ControlButton 
                type="button" 
                onClick={() => handleIncrement('workdaysPerMonth')}
              >
                +
              </ControlButton>
              <ControlButton 
                type="button" 
                onClick={() => handleDecrement('workdaysPerMonth')}
              >
                -
              </ControlButton>
            </InputControls>
          </InputWrapper>
        </FormGroup>
        
        <SubmitButton type="submit">上班</SubmitButton>
      </Form>
    </PageContainer>
  );
};

export default InputPage; 